/**
 * AxLabs Experiments - Reaction Kinetics (Arrhenius)
 *
 * Demonstrates chemical reaction rate laws and temperature dependence.
 *
 * Governing equations:
 * - Rate law: rate = k[A]^n[B]^m
 * - Arrhenius: k = A * e^(-Ea/RT)
 * - Integrated rate laws for 0th, 1st, 2nd order
 */
import { BaseSimulationEngine, DIMENSIONS, rk4StepNumeric, } from '@axlabs/simulation-core';
// ============================================================================
// CONSTANTS
// ============================================================================
/** Gas constant R = 8.314 J/(mol·K) */
const R_GAS = 8.314;
// ============================================================================
// PARAMETERS
// ============================================================================
const PARAMETERS = [
    {
        id: 'initialConc',
        name: 'Initial Concentration [A]₀',
        symbol: '[A]_0',
        unit: 'M',
        dimension: DIMENSIONS.CONCENTRATION,
        min: 0.01,
        max: 10,
        default: 1,
        step: 0.1,
        description: 'Starting concentration of reactant A',
    },
    {
        id: 'reactionOrder',
        name: 'Reaction Order',
        symbol: 'n',
        unit: '',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 0,
        max: 2,
        default: 1,
        step: 0.5,
        description: 'Order with respect to reactant A (0, 0.5, 1, 1.5, or 2)',
    },
    {
        id: 'preExponential',
        name: 'Pre-exponential Factor',
        symbol: 'A',
        unit: '1/s',
        dimension: DIMENSIONS.FREQUENCY,
        min: 1e5,
        max: 1e15,
        default: 1e10,
        step: 1e9,
        description: 'Arrhenius pre-exponential factor',
    },
    {
        id: 'activationEnergy',
        name: 'Activation Energy',
        symbol: 'E_a',
        unit: 'kJ/mol',
        dimension: { L: 2, M: 1, T: -2, I: 0, Θ: 0, N: -1, J: 0 },
        min: 10,
        max: 200,
        default: 50,
        step: 5,
        description: 'Energy barrier for reaction',
    },
    {
        id: 'temperature',
        name: 'Temperature',
        symbol: 'T',
        unit: 'K',
        dimension: DIMENSIONS.TEMPERATURE,
        min: 200,
        max: 600,
        default: 298,
        step: 5,
        description: 'Absolute temperature',
    },
];
// ============================================================================
// EQUATIONS
// ============================================================================
const EQUATIONS = [
    {
        id: 'arrhenius',
        name: 'Arrhenius Equation',
        latex: 'k = A \\cdot e^{-E_a/(RT)}',
        variables: ['rateConstant', 'preExponential', 'activationEnergy', 'temperature'],
        description: 'Temperature dependence of rate constant',
    },
    {
        id: 'rate_law',
        name: 'Rate Law',
        latex: 'rate = k[A]^n',
        variables: ['rate', 'rateConstant', 'concA', 'reactionOrder'],
        description: 'Instantaneous reaction rate',
    },
    {
        id: 'first_order',
        name: 'First-Order Integrated',
        latex: '\\ln[A] = \\ln[A]_0 - kt',
        variables: ['concA', 'initialConc', 'rateConstant'],
        description: 'Concentration vs time for first-order',
        condition: (params) => (params['reactionOrder'] ?? 1) === 1,
    },
    {
        id: 'second_order',
        name: 'Second-Order Integrated',
        latex: '\\frac{1}{[A]} = \\frac{1}{[A]_0} + kt',
        variables: ['concA', 'initialConc', 'rateConstant'],
        description: 'Concentration vs time for second-order',
        condition: (params) => (params['reactionOrder'] ?? 1) === 2,
    },
    {
        id: 'zero_order',
        name: 'Zero-Order Integrated',
        latex: '[A] = [A]_0 - kt',
        variables: ['concA', 'initialConc', 'rateConstant'],
        description: 'Concentration vs time for zero-order',
        condition: (params) => (params['reactionOrder'] ?? 1) === 0,
    },
    {
        id: 'half_life_first',
        name: 'Half-Life (First-Order)',
        latex: 't_{1/2} = \\frac{\\ln 2}{k} = \\frac{0.693}{k}',
        variables: ['halfLife', 'rateConstant'],
        description: 'Time for concentration to halve (first-order)',
        condition: (params) => (params['reactionOrder'] ?? 1) === 1,
    },
    {
        id: 'half_life_second',
        name: 'Half-Life (Second-Order)',
        latex: 't_{1/2} = \\frac{1}{k[A]_0}',
        variables: ['halfLife', 'rateConstant', 'initialConc'],
        description: 'Half-life depends on initial concentration',
        condition: (params) => (params['reactionOrder'] ?? 1) === 2,
    },
    {
        id: 'arrhenius_linear',
        name: 'Arrhenius Plot',
        latex: '\\ln k = \\ln A - \\frac{E_a}{R} \\cdot \\frac{1}{T}',
        variables: ['rateConstant', 'preExponential', 'activationEnergy', 'temperature'],
        description: 'ln(k) vs 1/T gives straight line with slope -Ea/R',
    },
];
// ============================================================================
// FAILURE MODES
// ============================================================================
const FAILURE_MODES = [
    {
        id: 'reactant_depleted',
        name: 'Reactant Depleted',
        description: 'Reactant has been consumed',
        check: (state) => (state.variables['concA'] ?? 1) < 0.001,
        severity: 'warning',
        educationalMessage: 'The reactant has been nearly completely consumed. ' +
            'Reaction has essentially gone to completion.',
    },
    {
        id: 'very_slow_reaction',
        name: 'Very Slow Reaction',
        description: 'Reaction is proceeding extremely slowly',
        check: (state) => {
            const rate = state.variables['rate'] ?? 0;
            const conc = state.variables['concA'] ?? 1;
            return rate < 1e-10 && conc > 0.1;
        },
        severity: 'warning',
        educationalMessage: 'The reaction is proceeding extremely slowly. ' +
            'Try increasing temperature or using a catalyst (lower Ea).',
    },
];
// ============================================================================
// EXPERIMENT CONFIG
// ============================================================================
export const REACTION_KINETICS_CONFIG = {
    id: 'reaction-kinetics',
    slug: 'reaction-kinetics',
    name: 'Reaction Kinetics (Arrhenius)',
    domain: 'chemistry',
    description: 'Explore reaction rates and temperature dependence',
    longDescription: `
Chemical kinetics studies how fast reactions proceed and what factors affect the rate.

Key concepts:
- Reaction order: how rate depends on concentration
- Rate constant k: proportionality factor
- Arrhenius equation: k depends exponentially on temperature
- Activation energy: energy barrier for reaction
- Pre-exponential factor: related to collision frequency

Reaction orders:
- Zero order: rate independent of concentration
- First order: rate proportional to [A]
- Second order: rate proportional to [A]²

Temperature effect:
- Higher T → larger k → faster reaction
- Every 10°C increase roughly doubles rate (rule of thumb)
- Arrhenius plot (ln k vs 1/T) gives Ea from slope
  `.trim(),
    version: 1,
    conceptTags: ['kinetics', 'rate-law', 'arrhenius', 'activation-energy', 'half-life'],
    difficulty: 4,
    prerequisites: ['chemical-reactions', 'exponentials'],
    parameters: PARAMETERS,
    equations: EQUATIONS,
    initialConditions: [
        { variableId: 'concA', type: 'parameter', parameterId: 'initialConc' },
        { variableId: 'concP', type: 'fixed', value: 0 },
    ],
    failureModes: FAILURE_MODES,
    recommendedDt: 0.01,
    maxDt: 0.1,
};
// ============================================================================
// SIMULATION ENGINE
// ============================================================================
export class ReactionKineticsSimulation extends BaseSimulationEngine {
    config = REACTION_KINETICS_CONFIG;
    constructor() {
        super({ concA: 1, concP: 0, rate: 0, rateConstant: 0, halfLife: 0 }, { type: 'rk4', dt: 0.01, maxSubsteps: 10 });
    }
    /**
     * Calculate rate constant using Arrhenius equation
     */
    calculateK(params) {
        const A = params['preExponential'] ?? 1e10;
        const Ea_kJ = params['activationEnergy'] ?? 50;
        const Ea = Ea_kJ * 1000; // Convert to J/mol
        const T = params['temperature'] ?? 298;
        return A * Math.exp(-Ea / (R_GAS * T));
    }
    /**
     * Calculate reaction rate
     */
    calculateRate(concA, params) {
        const k = this.calculateK(params);
        const n = params['reactionOrder'] ?? 1;
        // Prevent negative concentrations affecting rate
        const safeConc = Math.max(0, concA);
        return k * Math.pow(safeConc, n);
    }
    getInitialState(params) {
        const A0 = params['initialConc'] ?? 1;
        const k = this.calculateK(params);
        const n = params['reactionOrder'] ?? 1;
        const rate = this.calculateRate(A0, params);
        let halfLife;
        if (n === 1) {
            halfLife = Math.LN2 / k;
        }
        else if (n === 2) {
            halfLife = 1 / (k * A0);
        }
        else if (n === 0) {
            halfLife = A0 / (2 * k);
        }
        else {
            // General formula for half-life
            halfLife = (Math.pow(2, n - 1) - 1) / ((n - 1) * k * Math.pow(A0, n - 1));
        }
        return {
            concA: A0,
            concP: 0,
            rate,
            rateConstant: k,
            halfLife: Number.isFinite(halfLife) ? halfLife : 0,
        };
    }
    computeDerivatives(state, params) {
        const rate = this.calculateRate(state.concA, params);
        return {
            concA: -rate,
            concP: rate,
        };
    }
    integrate(dt) {
        const params = this.parameters;
        const n = params['reactionOrder'] ?? 1;
        const k = this.calculateK(params);
        // Use analytical solution where possible for stability
        if (n === 1 && this.state.concA > 0) {
            // First order: [A] = [A]₀ * e^(-kt)
            const A0 = this.state.concA;
            const newConcA = A0 * Math.exp(-k * dt);
            this.state.concP += (A0 - newConcA);
            this.state.concA = newConcA;
        }
        else if (n === 0) {
            // Zero order: [A] = [A]₀ - kt
            const consumed = k * dt;
            if (this.state.concA > consumed) {
                this.state.concA -= consumed;
                this.state.concP += consumed;
            }
            else {
                this.state.concP += this.state.concA;
                this.state.concA = 0;
            }
        }
        else if (n === 2 && this.state.concA > 0) {
            // Second order: 1/[A] = 1/[A]₀ + kt
            const invA0 = 1 / this.state.concA;
            const newConcA = 1 / (invA0 + k * dt);
            this.state.concP += (this.state.concA - newConcA);
            this.state.concA = Math.max(0, newConcA);
        }
        else {
            // General case: use RK4
            const derivative = (s, _t) => {
                const rate = this.calculateRate(s.concA, params);
                return {
                    concA: -rate,
                    concP: rate,
                    rate: 0,
                    rateConstant: 0,
                    halfLife: 0,
                };
            };
            const newState = rk4StepNumeric(this.state, derivative, this.time, dt);
            this.state.concA = Math.max(0, newState.concA ?? 0);
            this.state.concP = Math.max(0, newState.concP ?? 0);
        }
        // Update derived values
        this.state.rate = this.calculateRate(this.state.concA, params);
        this.state.rateConstant = k;
        // Update half-life (for first order, constant; for others, depends on current [A])
        if (n === 1) {
            this.state.halfLife = Math.LN2 / k;
        }
        else if (n === 2 && this.state.concA > 0) {
            this.state.halfLife = 1 / (k * this.state.concA);
        }
        else if (n === 0 && k > 0) {
            this.state.halfLife = this.state.concA / (2 * k);
        }
    }
    getPositionForStabilityCheck() {
        return this.state.concA;
    }
    /**
     * Generate Arrhenius plot data (ln k vs 1/T)
     */
    generateArrheniusData(temperatures) {
        return temperatures.map(T => {
            const params = { ...this.parameters, temperature: T };
            const k = this.calculateK(params);
            return {
                invT: 1000 / T, // 1000/T for nicer axis values
                lnK: Math.log(k),
            };
        });
    }
    /**
     * Calculate Q10 (factor by which rate increases for 10°C rise)
     */
    getQ10() {
        const T = this.parameters['temperature'] ?? 298;
        const k1 = this.calculateK(this.parameters);
        const k2 = this.calculateK({ ...this.parameters, temperature: T + 10 });
        return k2 / k1;
    }
}
/**
 * Factory function
 */
export function createReactionKinetics() {
    return new ReactionKineticsSimulation();
}
//# sourceMappingURL=reaction-kinetics.js.map